import { useId, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import ProductLineItem from '_components/ProductLineCarousel/ProductLineItem';
import Carousel, { useCarousel } from '_components/Carousel';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';
import { ProductLineCarouselType } from '@adapters/prismic/types/plp';
import { AlgoliaFacets } from '@utils/algolia/knownFacetNames';

import styles from './ProductLineCarousel.module.scss';

export interface ProductLineCarouselProps {
  title?: string;
  productLineCarousel: ProductLineCarouselType;
  headerRef?: React.RefObject<HTMLElement | null>;
}

interface ProductLineCarouselInnerProps {
  title?: string;
  headingId: string;
  items: ProductLineCarouselType['items'];
  imageRatio?: string;
  selectedIndex: number | null;
  onItemSelect: (index: number, productLineName: string) => void;
}

function ProductLineCarouselInner({
  title,
  headingId,
  items,
  imageRatio,
  selectedIndex,
  onItemSelect,
}: ProductLineCarouselInnerProps) {
  const { t } = useTranslation('common');
  const {
    state: { isPrevVisible, isNextVisible },
  } = useCarousel();

  const showArrows = isPrevVisible || isNextVisible;

  return (
    <>
      <div className={styles.header}>
        {title && (
          <h2 className={styles.heading} id={headingId}>
            {title}
          </h2>
        )}
        {showArrows && (
          <div className={styles.buttonGroup}>
            <Carousel.NavPrev
              aria-label={t('productStripDeals.navPrev')}
              className={styles.navButton}
            />
            <Carousel.NavNext
              aria-label={t('productStripDeals.navNext')}
              className={styles.navButton}
            />
          </div>
        )}
      </div>

      <Carousel.Track
        className={styles.swiper}
        spaceBetween={12}
        breakpoints={{
          0: {
            slidesPerView: 1.75,
          },
          400: {
            slidesPerView: 2.25,
          },
          480: {
            slidesPerView: 2.75,
          },
          704: {
            slidesPerView: 3.25,
          },
          901: {
            slidesPerView: 3.75,
          },
          1061: {
            slidesPerView: 5.25,
          },
        }}
      >
        {items?.map((item, index) => (
          <Carousel.Slide
            key={`product-line-carousel-${item?.product_line_name}-${index}`}
            className={styles.slide}
          >
            <ProductLineItem
              item={item}
              index={index}
              isSelected={selectedIndex === index}
              onSelect={onItemSelect}
              imageRatio={imageRatio}
              carouselTitle={title}
            />
          </Carousel.Slide>
        ))}
      </Carousel.Track>
    </>
  );
}

const ProductLineCarousel = ({
  title,
  productLineCarousel,
  headerRef,
}: ProductLineCarouselProps) => {
  const headingId = useId();
  const { items, imageRatio } = productLineCarousel;
  const { refine } = useRefinementList({
    attribute: AlgoliaFacets.Collection,
    limit: 100,
  });
  const { indexUiState } = useInstantSearch();

  const refinedCollections = useMemo(
    () => indexUiState.refinementList?.[AlgoliaFacets.Collection] ?? [],
    [indexUiState.refinementList]
  );

  const selectedIndex = useMemo(() => {
    const index =
      items?.findIndex(
        (item) =>
          !!item?.product_line_name &&
          refinedCollections.includes(item.product_line_name)
      ) ?? -1;
    return index === -1 ? null : index;
  }, [items, refinedCollections]);

  if (!items?.length) return null;

  const handleItemSelect = (index: number, productLineName: string) => {
    const isCurrentlySelected =
      !!productLineName && refinedCollections.includes(productLineName);

    refinedCollections.forEach((value) => refine(value));

    if (productLineName && !isCurrentlySelected) {
      refine(productLineName);
    }

    if (headerRef?.current) {
      const yOffset = -190;
      const y =
        headerRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section
      className={styles.root}
      {...(title ? { 'aria-labelledby': headingId } : {})}
    >
      <div className={styles.container}>
        <Carousel.Provider>
          <ProductLineCarouselInner
            title={title}
            headingId={headingId}
            items={items}
            imageRatio={imageRatio}
            selectedIndex={selectedIndex}
            onItemSelect={handleItemSelect}
          />
        </Carousel.Provider>
      </div>
    </section>
  );
};

export default ProductLineCarousel;
